import { useState, useMemo, useCallback } from 'react';
import { VisibilityState } from '@tanstack/react-table';
import { ColumnDef } from '@tanstack/react-table';
import { ViewType } from '../types';

type ColumnWithMeta = ColumnDef<any, any> & {
  visible?: boolean;
  accessorKey?: string;
  id?: string;
};

interface UseColumnVisibilityProps {
  orderColumns: ColumnWithMeta[];
  productColumns: ColumnWithMeta[];
  viewType: ViewType;
}

/**
 * Custom hook to manage column visibility state for tables
 */
export function useColumnVisibility({
  orderColumns,
  productColumns,
  viewType
}: UseColumnVisibilityProps) {
  // Initialize visibility states based on column definitions
  const initialOrderVisibility = useMemo(() => {
    const state: VisibilityState = {};
    orderColumns.forEach(column => {
      const key = typeof column.accessorKey === 'string' 
        ? column.accessorKey 
        : column.id as string;
      state[key] = column.visible !== false;
    });
    return state;
  }, [orderColumns]);

  const initialProductVisibility = useMemo(() => {
    const state: VisibilityState = {};
    productColumns.forEach(column => {
      const key = typeof column.accessorKey === 'string' 
        ? column.accessorKey 
        : column.id as string;
      state[key] = column.visible !== false;
    });
    return state;
  }, [productColumns]);

  // State
  const [orderColumnVisibility, setOrderColumnVisibility] = useState<VisibilityState>(initialOrderVisibility);
  const [productColumnVisibility, setProductColumnVisibility] = useState<VisibilityState>(initialProductVisibility);

  // Handler for toggling column visibility
  const handleToggleColumn = useCallback((columnId: string, visible: boolean) => {
    if (viewType === 'orders') {
      setOrderColumnVisibility(prev => ({
        ...prev,
        [columnId]: visible
      }));
    } else {
      setProductColumnVisibility(prev => ({
        ...prev,
        [columnId]: visible
      }));
    }
  }, [viewType]);

  // Get the current visibility state based on viewType
  const currentVisibility = useMemo(() => 
    viewType === 'orders' ? orderColumnVisibility : productColumnVisibility,
  [viewType, orderColumnVisibility, productColumnVisibility]);

  // Calculate visible columns for both views separately
  const visibleOrderColumns = useMemo(() => {
    return orderColumns.filter(column => {
      const columnId = typeof column.accessorKey === 'string'
        ? column.accessorKey
        : column.id as string;
      return orderColumnVisibility[columnId] !== false;
    });
  }, [orderColumns, orderColumnVisibility]);
  
  const visibleProductColumns = useMemo(() => {
    return productColumns.filter(column => {
      const columnId = typeof column.accessorKey === 'string'
        ? column.accessorKey
        : column.id as string;
      return productColumnVisibility[columnId] !== false;
    });
  }, [productColumns, productColumnVisibility]);
  
  // Get visible columns based on the current view type without recalculating
  const visibleColumns = useMemo(() => 
    viewType === 'orders' ? visibleOrderColumns : visibleProductColumns,
  [viewType, visibleOrderColumns, visibleProductColumns]);

  // Build options for the column visibility dropdown
  const columnOptions = useMemo(() => {
    const columns = viewType === 'orders' ? orderColumns : productColumns;
    const visibility = currentVisibility;
    
    return columns.map(column => {
      const columnId = typeof column.accessorKey === 'string' 
        ? column.accessorKey 
        : column.id as string;
      
      return {
        id: columnId,
        label: column.header as string,
        visible: visibility[columnId] !== false
      };
    });
  }, [viewType, orderColumns, productColumns, currentVisibility]);

  return {
    visibleColumns,
    columnOptions,
    handleToggleColumn,
    orderColumnVisibility,
    productColumnVisibility,
    setOrderColumnVisibility,
    setProductColumnVisibility
  };
}
